import { ColorConfig } from './types/RemoteConfig'

export type TintingIntensity =
  | { kind: 'full' }
  | { kind: 'single'; value: number }
  | { kind: 'gradient'; from: number; to: number }

export type DecodedTinting = {
  colorCode: string
  intensity: TintingIntensity
  intensityLabel: string | null
  colorName: string | null
  colorNameKey: string | null
  color: ColorConfig | null
}

const GRADIENT_CODE_RE = /^G(\d{2})(\d{2})$/
const DIGITS_SUFFIX_RE = /^\d+$/

const GRADIENT_CODE_OVERRIDES: Record<string, { from: number; to: number }> = {
  G7530: { from: 75, to: 35 },
}

const parseGradientCode = (
  gradientCode: string
): Extract<TintingIntensity, { kind: 'gradient' }> | null => {
  const override = GRADIENT_CODE_OVERRIDES[gradientCode]
  if (override) {
    return { kind: 'gradient', ...override }
  }
  const match = gradientCode.match(GRADIENT_CODE_RE)
  if (!match) {
    return null
  }
  return {
    kind: 'gradient',
    from: parseInt(match[1], 10),
    to: parseInt(match[2], 10),
  }
}

const parseIntensitySuffix = (suffix: string): TintingIntensity => {
  if (!suffix) {
    return { kind: 'full' }
  }
  const gradient = parseGradientCode(suffix)
  if (gradient) {
    return gradient
  }
  if (DIGITS_SUFFIX_RE.test(suffix)) {
    return { kind: 'single', value: parseInt(suffix, 10) }
  }
  return { kind: 'full' }
}

const getIntensityLabel = (intensity: TintingIntensity): string | null => {
  if (intensity.kind === 'single') {
    return `${intensity.value}%`
  }
  if (intensity.kind === 'gradient') {
    return `${intensity.from}% - ${intensity.to}%`
  }
  return null
}

const getColorNameKey = (name: string) => `steps:color.items.${name}.title`

const matchColor = (code: string, colors: Record<string, ColorConfig>) =>
  Object.values(colors)
    .filter((color) => color.code && code.startsWith(color.code.toUpperCase()))
    .sort((a, b) => b.code.length - a.code.length)[0] ?? null

export const decodeTinting = (
  code: string | null | undefined,
  colors: Record<string, ColorConfig>
): DecodedTinting | null => {
  const trimmed = code?.trim().toUpperCase()
  if (!trimmed) {
    return null
  }

  const color = matchColor(trimmed, colors)
  const colorCode = color?.code ?? trimmed
  const intensity: TintingIntensity = color
    ? parseIntensitySuffix(trimmed.slice(color.code.length))
    : { kind: 'full' }

  return {
    colorCode,
    intensity,
    intensityLabel: getIntensityLabel(intensity),
    colorName: color?.name ?? null,
    colorNameKey: color ? getColorNameKey(color.name) : null,
    color,
  }
}
